<template>
  <div>
    <v-data-table
      ref="dataTable"
      :headers="checkBoxColumn ? headersWithSelect : validHeaders"
      hide-default-footer
      :items="loadingItems || items"
      :item-key="itemKey"
      :options.sync="viewPagination"
      calculate-widths
      class="sn-table mt-4 rounded-corners"
      :class="{ 'modal-table': modalTable, 'hide-no-data': hideNoData, 'clickable-rows': (clickableRows && items.length > 0), 'top-align': topAlign}"
      data-cy="table-overview"
      :fixed-header="modalTable"
      :loading="!useSkeletonLoader && loading"
      :item-class="rowDisabled"

      @click:row="clickableRows ? handleRowClick($event) : null"
    >
      <!-- Table Action Bar -->
      <template
        v-if="showActionBar"
        v-slot:top
      >
        <!-- Action Buttons -->
        <sn-table-action-bar
          :primary-action="primaryAction"
          :secondary-action="secondaryAction"
          :tertiary-actions="tertiaryActions"
          :search-enabled="searchEnabled"
          :search-options="searchOptions"
          :filter-summaries="filterSummaries"
          :sn-input-props_pendo="snInputProps_pendo"
          @primary-action="$emit('primary-action')"
          @secondary-action="$emit('secondary-action')"
          @tertiary-action="$emit('tertiary-action', $event)"
          @search-table="searchTable"
          @clear-filter="clearFilter"
          @sort-filter-changed="applySortFilterChange"
        />
      </template>

      <!-- Checkbox Type Columns -->
      <template
        v-for="col in toggleCols"
        v-slot:[`item.${col}`]="{item, header}"
      >
        <v-switch
          v-if="header.type === 'toggle'"
          :key="col"
          class="sn-switch"
          :ripple="false"
          :input-value="item[header.value]"
          color="#000"
          inset
          flat
          :data-cy="`${item.name ? item.name : 'table'}-switch`"
          data-test="switch"
          @click.stop
          @change="toggled(item)"
        />
        <v-simple-checkbox
          v-else-if="header.type === 'checkbox'"
          :key="col"
          :value="item[header.value]"
          off-icon="$snCheckBox"
          on-icon="$snCheckBoxSelected"
          color="black"
          @change="toggled(item)"
        />
      </template>

      <!-- Item Action Header -->
      <template
        v-if="showItemActionsHeader"
        v-slot:header
      >
        <sn-table-item-actions-header
          :actions="multipleActionItems"
          :headers="checkBoxColumn ? headersWithSelect : validHeaders"
          :num-selected="selectedItemIds.length"
          :indeterminate="indeterminate"
          :sn-input-props_pendo="snInputProps_pendo"
          :all-items-on-page-selected="allItemsOnPageSelected"
          @header-select="toggleSelectAll"
          @header-action="headerAction"
        />
      </template>

      <!-- Select All Checkbox -->
      <template
        v-if="checkBoxColumn && !radioSelect"
        v-slot:header.selected
      >
        <v-simple-checkbox
          v-ripple
          class="sn-table-item-actions--select-all__checkbox"
          :value="anyItemsOnPageSelected"
          :indeterminate="indeterminate"
          off-icon="$snCheckBox"
          on-icon="$snCheckBoxSelected"
          indeterminate-icon="$snCheckBoxIndeterminate"
          data-cy="table-header-select-all-checkbox"
          :data-pendo="snInputProps_pendo + '-SelectAll'"
          @input="toggleSelectAll"
        />
      </template>

      <!-- Single Item Checkbox -->
      <template
        v-if="checkBoxColumn"
        v-slot:item.selected="{ item }"
      >
        <v-simple-checkbox
          :value="selectedItemIds.includes(item[itemKey])"
          off-icon="$snCheckBox"
          on-icon="$snCheckBoxSelected"
          :data-cy="`${checkboxName(item)}-checkbox`"
          data-test="row-items"
          :data-pendo="snInputProps_pendo + '-SelectRow'"
          @input="toggleSingleSelect(item[itemKey])"
        />
      </template>

      <!-- Actions Column -->
      <template
        v-if="rowActionsColumn"
        v-slot:item.actions="{ item }"
        class="d-flex"
      >
        <div v-if="actionsWithText(item.actions)">
          <div
            v-for="(action, index) in displayableActions(item.actions)"
            :key="action.eventToEmit"
            class="d-flex"
          >
            <v-icon
              :class="`${index < item.actions.length - 1 ? 'mr-3' : ''}`"
              :color="action.color"
              size="18px"
              :data-pendo="snInputProps_pendo + '-' + action.eventToEmit"
              @click.stop="$emit(action.eventToEmit, item)"
              v-text="action.icon"
            />
            <div
              class="action-text-section"
              @click.stop="$emit(action.eventToEmit, item)"
            >
              {{ action.text }}
            </div>
          </div>
        </div>
        <div v-else>
          <v-icon
            v-for="(action, index) in displayableActions(item.actions)"
            :key="action.eventToEmit"
            :class="`${index < item.actions.length - 1 ? 'mr-3' : ''}`"
            :color="action.color"
            size="18px"
            :data-cy="(snInputProps_identifier || action.eventToEmit) + '-btn'"
            :data-pendo="snInputProps_pendo + '-' + action.eventToEmit"
            @click.stop="$emit(action.eventToEmit, item)"
            v-text="action.icon"
          />
        </div>
      </template>

      <!-- Icons Column -->
      <template
        v-if="rowIconsColumn"
        v-slot:item.icons="{ item }"
      >
        <div class="d-flex flex-row text-start">
          <div
            v-for="(icon, index) in item.icons"
            :key="index"
          >
            <sn-tooltip
              v-if="icon.tooltip"
              direction="top"
            >
              <template v-slot:activator>
                <v-icon
                  :class="`${index < item.icons.length - 1 ? 'mr-3' : ''}`"
                  :color="icon.color"
                  size="24px"
                  v-text="icon.icon"
                />
              </template>
              <span class="sn-tooltip">{{ icon.tooltip }}</span>
            </sn-tooltip>
            <v-icon
              v-else
              :class="`${index < item.icons.length - 1 ? 'mr-3' : ''}`"
              :color="icon.color"
              size="24px"
              v-text="icon.icon"
            />
          </div>
        </div>
      </template>

      <!-- Headers -->
      <template
        v-for="header in headersNeedingFilterMenu"
        v-slot:[`header.${header.value}`]
      >
        <sn-table-column-header
          :key="header.value"
          :header="header"
          :active-sort="activeSort"
          :active-filters="activeFilters"
          @change="applySortFilterChange"
          @search-on-filter="applySearchOnFilter"
          @load-more-filters="$emit('load-more-filters', header.value)"
        />
      </template>

      <!-- Overflow Actions Column -->
      <template v-slot:item.overflowActions="{ item }">
        <sn-table-item-overflow-actions
          v-if="filteredItemOverflowActions(item).length"
          :item="item"
          :item-key="itemKey"
          :item-overflow-actions="filteredItemOverflowActions(item)"
          v-on="$listeners"
        />
      </template>

      <!-- Skeleton Loader -->
      <template
        v-if="useSkeletonLoader && loading && validHeaders.length"
        v-slot:item
      >
        <tr>
          <td
            v-for="(header, headerIndex) in validHeaders"
            :key="`loading-header-${headerIndex}`"
            style="height: 54px"
          >
            <v-skeleton-loader
              :width="getRandomSkeletonLoaderWidth()"
              type="text"
            />
          </td>
        </tr>
      </template>

      <!-- Empty State -->
      <template v-slot:no-data>
        <div>
          <v-icon
            size="150"
          >
            $snEmptyBox
          </v-icon>
          <p class="sn-body-1">
            {{ emptyListText }}
          </p>
        </div>
      </template>

      <!-- Other Slots -->
      <template
        v-for="slot in Object.keys($scopedSlots)"
        v-slot:[slot]="props"
      >
        <slot
          :name="slot"
          v-bind="props"
        />
      </template>

      <!-- Pagination -->
      <template v-slot:footer="{}">
        <!-- Loading State -->
        <div
          v-if="useSkeletonLoader && loading"
          class="snsl-table-tfoot"
        >
          <v-skeleton-loader
            width="50px"
            type="text"
            class="mr-2"
          />
          <v-skeleton-loader
            width="100px"
            type="text"
          />
        </div>

        <div
          v-else-if="showPagination"
          class="d-flex flex-row-reverse py-6"
        >
          <sn-pagination
            :pagination="pagination || viewPagination"
            :per-page-options="perPageOptions"
            :filtered-total="calculatedTotalNumberOfItems"
            :sn-input-props_pendo="snInputProps_pendo"
            @pagination="assignPagination"
          />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import SnTableActionBar from './snTableActionBar.vue'
import SnTableItemActionsHeader from './snTableItemActionsHeader.vue'
import SnTableItemOverflowActions from './snTableItemOverflowActions.vue'
import SnTableColumnHeader from './snTableColumnHeader.vue'
import SnTooltip from '../../snTooltip/snTooltip.vue'
import SnPagination from '~/components/snui/v2/snPagination/snPagination'
import paginationMixin2 from '~/components/common/mixins/paginationMixin2'
import SnInputProps from '~/components/snui/v2/props/SnInputProps'
import deprecatedMixin from '../deprecatedMixin'

export default {
  name: 'SnTableDeprecated',
  components: {
    SnTableActionBar,
    SnTableItemActionsHeader,
    SnTableItemOverflowActions,
    SnTableColumnHeader,
    SnTooltip,
    SnPagination
  },
  mixins: [
    paginationMixin2,
    deprecatedMixin
  ],
  props: {
    ...SnInputProps,
    headers: {
      type: Array,
      required: true,
      validator (prop) {
        return prop.every(item => item.sortable !== undefined && item.value !== undefined)
      }
    },
    items: {
      type: Array,
      required: true
    },
    itemKey: {
      type: String,
      required: false,
      default: 'id'
    },
    preSelectedItemIds: {
      type: Array,
      required: false,
      default: () => []
    },
    multipleActionItems: {
      type: Array,
      required: false,
      default: () => []
    },
    primaryAction: {
      type: Object,
      required: false,
      default () { return null }
    },
    secondaryAction: {
      type: Object,
      required: false,
      default () { return null }
    },
    tertiaryActions: {
      type: Array,
      required: false,
      default () { return null }
    },
    rowActionsColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    rowIconsColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    itemOverflowActions: {
      type: Array,
      required: false,
      default: () => []
    },
    checkBoxColumn: {
      type: Boolean,
      required: false,
      default: false
    },
    searchEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    searchOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          label: 'Search',
          text: ''
        }
      }
    },
    pagination: {
      type: Object,
      required: false,
      default: undefined
    },
    hidePagination: {
      type: Boolean,
      required: false,
      default: false
    },
    hideNoData: {
      type: Boolean,
      required: false,
      default: false
    },
    initialItemsPerPage: {
      type: Number,
      required: false,
      default: 10
    },
    perPageOptions: {
      type: Array,
      required: false,
      default: () => [10, 30, 50]
    },
    filteredTotal: {
      type: Number,
      required: false,
      default: 0
    },
    modalTable: {
      type: Boolean,
      required: false,
      default: false
    },
    emptyListText: {
      type: String,
      required: false,
      default: 'No data available'
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    clickableRows: {
      type: Boolean,
      required: false,
      default: false
    },
    hidePaginationWhenNoPages: {
      type: Boolean,
      required: false,
      default: false
    },
    numberOfLoadingRows: {
      type: Number,
      required: false,
      default: 10
    },
    useSkeletonLoader: {
      type: Boolean,
      required: false,
      default: false
    },
    radioSelect: {
      type: Boolean,
      required: false,
      default: false
    },
    renderTile: {
      type: Function,
      default () {
        return (_actionItem, _item) => { return true }
      },
      required: false
    },
    topAlign: {
      type: Boolean,
      required: false,
      default: false
    },
    activeSort: {
      type: String,
      required: false,
      default: ''
    },
    activeFilters: {
      type: Array,
      required: false,
      default: () => []
    },
    useBasicPagination: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      selectedItemIds: [],
      toggleChanged: false,
      deprecatedMixin: {
        alternativeComponent: 'SnTable'
      }
    }
  },
  computed: {
    showPagination () {
      if (this.hidePagination) return false
      if (this.hidePaginationWhenNoPages && this.items.length < this.initialItemsPerPage) return false
      return !this.hidePagination
    },
    calculatedTotalNumberOfItems () {
      return this.filteredTotal || this.items.length
    },
    loadingItems () {
      if (!this.loading || !this.useSkeletonLoader) {
        return null
      }

      const loadingRows = []

      for (let i = 0; i < this.numberOfLoadingRows; i++) {
        loadingRows.push({ name: 'loadingRow' })
      }

      return loadingRows
    },
    toggleCols () {
      return this.validHeaders
        .filter(header => ['toggle', 'checkbox'].includes(header.type))
        .map(header => header.value)
    },
    headersNeedingFilterMenu () {
      return this.validHeaders.filter(f => f.sortBy || f.filterBy)
    },
    filterSummaries () {
      return this.validHeaders
        .map(h => ({
          header: h,
          activeFilters: this.activeFilters.filter(fv => h.filterBy?.nodes?.some(fb => fb.value === fv)),
          activeSort: this.activeSort
        }))
        .filter(a => a.activeFilters.length > 0)
    },
    indeterminate () {
      return this.anyItemsOnPageSelected && !this.allItemsOnPageSelected
    },
    showActionBar () {
      return this.searchEnabled || this.primaryAction || this.secondaryAction || this.tertiaryActions
    },
    showItemActionsHeader () {
      return this.multipleActionItems?.length > 0 && this.selectedItemIds.length > 0
    },
    validHeaders () {
      // ensure that each header has a 'sortable' property
      return this.headers.map(header => ({ ...header, sortable: !!header.sortable }))
    },
    headersWithSelect () {
      return [
        {
          text: '',
          value: 'selected',
          width: '5%',
          sortable: false
        },
        ...this.validHeaders
      ]
    },
    itemIdsOnCurrentPage () {
      if (this.viewPagination.itemsPerPage && this.useBasicPagination) {
        const startIndex = (this.viewPagination.page - 1) * this.viewPagination.itemsPerPage
        return this.items.slice(startIndex, startIndex + this.viewPagination.itemsPerPage).map(item => item[this.itemKey])
      }
      return this.items.map(item => item[this.itemKey])
    },
    allItemsOnPageSelected () {
      return this.itemIdsOnCurrentPage.every(item => this.selectedItemIds.includes(item))
    },
    anyItemsOnPageSelected () {
      return this.itemIdsOnCurrentPage.some(item => this.selectedItemIds.includes(item))
    }
  },
  watch: {
    /*
     * Mainly just needed for resetting pagination
     * when switching pagination on and off in storybook
     */
    hidePagination () {
      this.resetTablePagination()
    },
    items: {
      deep: true,
      handler () {
        this.selectedItemIds = []

        if (!this.toggleChanged && this.filteredTotal === 0) {
          // items watcher has not been triggered by a toggle option, and items are not
          // updating due to query pagination (or else filteredTotal would be set)
          this.resetTablePagination()
        } else {
          this.toggleChanged = false
        }
      }
    },
    preSelectedItemIds: {
      deep: true,
      handler () {
        this.selectedItemIds = [...new Set([...this.preSelectedItemIds, ...this.selectedItemIds])]
      }
    }
  },
  mounted () {
    this.applyHackForMultipleVAppInstances()
    this.resetTablePagination()
  },
  methods: {
    /**
     * If there are multiple v-app instances rendered on this page (which is currently the case for pages that use the
     * Navigation2.vue component), any v-menu components will be added to the v-app that is loaded first, instead of the v-app
     * instance that this snTable component belongs to. This in turn prevents the the `close-on-click` prop from working. The
     * following is a hack copied from https://gitlab.com/simplenexus-engineering/simplenexus.com/-/merge_requests/1340/diffs.
     * Definitely not ideal, but I think reducing the multiple v-app instances down to 1 is going to require a heavier lift and
     * coordination as a frontend guild.
     */
    applyHackForMultipleVAppInstances () {
      const appInstances = document.querySelectorAll('[data-app="true"]')
      if (appInstances.length === 1) return

      appInstances.forEach(appEl => {
        if (appEl === this.$root.$el) { return }
        appEl.removeAttribute('data-app')
      })
    },
    getRandomSkeletonLoaderWidth () {
      // Formula: Math.floor(Math.random() * (max - min + 1) + min)
      // Where min = 10 and max = 50
      return `${Math.floor((Math.random() * 41) + 10)}%`
    },
    toggled (item) {
      this.$emit('toggle-changed', item)
      this.toggleChanged = true
    },
    applySortFilterChange (values) {
      this.$emit('sort-filter-changed', values)
    },
    applySearchOnFilter (values) {
      this.$emit('search-on-filter', values)
    },
    clearFilter (filterSummary) {
      const newFilters = this.activeFilters.filter(af => !filterSummary.activeFilters.includes(af))
      this.$emit('sort-filter-changed', { activeFilters: newFilters, activeSort: this.activeSort })
    },
    resetTablePagination () {
      if (this.hidePagination) {
        this.viewPagination.itemsPerPage = this.calculatedTotalNumberOfItems
      } else {
        this.sn_paginationMixin2_resetPagination()
        this.viewPagination.itemsPerPage = this.initialItemsPerPage
      }
    },
    assignPagination (pagination) {
      this.$emit('pagination', pagination)
      this.viewPagination = pagination
    },
    searchTable (searchText) {
      this.$emit('search-table', searchText)
    },
    toggleSingleSelect (itemId) {
      if (this.radioSelect) {
        if (this.selectedItemIds.includes(itemId)) {
          this.selectedItemIds.splice(this.selectedItemIds.indexOf(itemId), 1)
        } else {
          this.selectedItemIds = []
          this.selectedItemIds.push(itemId)
        }
      } else if (this.selectedItemIds.includes(itemId)) {
        this.selectedItemIds = this.selectedItemIds.filter(selectedId => selectedId !== itemId)
      } else {
        this.selectedItemIds.push(itemId)
      }
      this.$emit('selected-item-ids-changed', this.selectedItemIds)
    },
    toggleSelectAll () {
      if (this.radioSelect) {
        // toggleSelectAll will only be an option with radioSelect if an item is already selected
        this.selectedItemIds = []
      } else if (this.allItemsOnPageSelected) {
        // remove the current page items from the selected items
        this.selectedItemIds = this.selectedItemIds.filter(item => !this.itemIdsOnCurrentPage.includes(item))
      } else {
        // add the current page items to the selected items (remove duplicates)
        const tmpItems = new Set(this.selectedItemIds.concat(this.itemIdsOnCurrentPage))
        this.selectedItemIds = Array.from(tmpItems)
      }
      this.$emit('selected-item-ids-changed', this.selectedItemIds)
    },
    headerAction (action) {
      this.$emit('header-action', {
        action,
        selectedItemIds: this.selectedItemIds
      })
    },
    displayableActions (actions) {
      return actions?.filter(a => a.eventToEmit)
    },
    actionsWithText (actions) {
      return actions?.filter(a => a.text).length > 0
    },
    handleRowClick (event) {
      try {
        pendo.track(this.snInputProps_pendo + '-RowClicked') // eslint-disable-line no-undef
      } catch (e) {
        if (e?.name !== 'ReferenceError') {
          this.$sentry.captureException(e)
        }
      }
      this.$emit('row-clicked', event)
    },
    rowDisabled (item) {
      return item.disabled ? 'disabled' : null
    },
    checkActionConditions (action) {
      return action.conditions ?? true
    },
    filteredItemOverflowActions (propsItem) {
      return this.itemOverflowActions.filter(item => this.renderTile(item, propsItem))
    },
    checkboxName (item) {
      const firstHeaderValue = this.validHeaders[0]?.value
      const itemName = item.name

      if (itemName) return itemName
      if (firstHeaderValue) return item[this.validHeaders[0]?.value]
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.sn-switch ::v-deep {
  :not(.v-input--is-label-active) .v-input--switch__track {
    color: $sn-gray-04;
  }

  .v-input--switch {
    &__track {
      border: none;
      opacity: 1;
    }

    &__thumb {
      width: 22px;
      height: 22px;
      top: calc(50% - 11px);
      margin-left: -1px;
    }
  }
}

.action-text-section {
  text-decoration: underline;
  cursor: pointer;
  padding-left: 7px;
}

.snsl-table-tfoot {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding: 16px;
}

.sn-table {
  border-collapse: collapse;
  width: 100%;

  ::v-deep .v-data-table__wrapper table {
    border-radius: 2px 2px 0 0;
    position: relative;

    thead tr th {
      background-color: $sn-brand-01 !important;
      color: $sn-primary-03 !important;
      font-family: $font-family-primary !important;
      font-size: 15px;
      font-weight: $font-weight-semi-bold !important;
      padding-left: 12px;
      padding-right: 12px;
      border-bottom: none !important;
      white-space: nowrap;
    }

    tbody tr {
      height: 48px;

      &:hover {
        background: transparent !important;
      }

      td:not(.v-data-table__mobile-row) {
        border-bottom: 2px solid $sn-gray-01 !important;
      }

      td {
        color: $sn-primary-03 !important;
        font-family: $font-family-primary;
        font-size: 15px;
        font-weight: $font-weight-regular;
        padding-left: 12px;
        padding-right: 12px;
      }
    }

    tbody .disabled {
      opacity: 0.4;
      pointer-events: none;
    }

    .actions-column {
      min-width: 48px;
    }

    .v-simple-checkbox {
      i, .v-icon {
        color: $sn-primary-03;
      }
    }
  }

  &.hide-no-data {
    ::v-deep .v-data-table__empty-wrapper {
      display: none;
    }
  }
}

.sn-table.top-align {
  ::v-deep .v-data-table__wrapper table {
    tbody tr td {
      vertical-align: top;
      padding-top: 8px;
      padding-bottom: 8px;
    }
  }
}

.sn-table.clickable-rows {
  ::v-deep .v-data-table__wrapper table {
    tbody tr {
      cursor: pointer;
    }
    tr:hover td {
      background-color: $sn-gray-01;
    }
  }
}

.sn-table.rounded-corners {
  ::v-deep .v-data-table__wrapper table {
    thead tr th:first-child{
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px
    }
    thead tr th:last-child{
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px
    }
    tbody tr td:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px
    }
    tbody tr td:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px
    }
  }
}

.sn-tooltip {
  font-family: $font-family-primary !important;
  font-weight: $font-weight-regular !important;
  color: $sn-white !important;
}
</style>
